import type { RemixLevelParameters } from 'editor/level/types';
import type { EditorGameAreaView } from 'editor/EditorGameAreaView';
import * as HTMLElementCreator from '../../libs/HTML/HTMLElementCreator';
import { EditorHeader, type EditorHeaderConfig } from './EditorHeader';
import { EditorItemSelector } from './panels/EditorItemSelector';
import { EditMode } from '../types';
import { EditorLevelPropertiesPanel } from './panels/EditorLevelPropertiesPanel';
import type { LevelData } from '../../game/level/LevelData';
import { EditorBlockersPanel } from './panels/EditorBlockersPanel';
import type { EditorPanel } from './panels/types';
import { EditorBlockerConnectionsPanel } from './panels/EditorBlockerConnectionsPanel';
import { EditorRemixPanel } from './panels/EditorRemixPanel';
import { TileColor } from '../../game/boardMVC/TileColor';
import { BlockerType } from '../../game/boardMVC/types';
import type { TileStackModel } from '../../game/boardMVC/models/board/TileStackModel';

export interface EditorOverlayConfig {
  headerConfig: EditorHeaderConfig;
  onStateChanged: (deltaState?: Partial<LevelData>) => void;
  getGameAreaView: () => EditorGameAreaView | undefined;
  refreshBlockerView: (stackModel: TileStackModel) => void;
  runLevelRemix: (remixParameters: RemixLevelParameters) => Promise<void>;
}

export class EditorOverlay {
  private readonly overlay: HTMLElement;

  private readonly header: EditorHeader;

  public readonly colorSelector: EditorItemSelector;

  public readonly blockerSelector: EditorItemSelector;

  public readonly utBlockerSelector: EditorItemSelector;

  public readonly propertiesPanel: EditorLevelPropertiesPanel;

  public readonly blockersPanel: EditorBlockersPanel;

  public readonly blockerConnectionsPanel: EditorBlockerConnectionsPanel;

  private readonly panels: { panel: EditorPanel; modes: EditMode[] }[];

  public readonly remixPanel: EditorRemixPanel;

  constructor({
    headerConfig,
    onStateChanged,
    getGameAreaView,
    refreshBlockerView,
    runLevelRemix,
  }: EditorOverlayConfig) {
    this.overlay = HTMLElementCreator.createDiv({
      elementID: 'editor',
      parentElement: document.body,
      style: {
        position: 'fixed',
        width: '100%',
        height: '100%',
        zIndex: '10000',
        pointerEvents: 'none',
        visibility: 'visible',
      },
    });

    this.header = this.initHeader(headerConfig);

    this.overlay.appendChild(this.header.element);

    this.propertiesPanel = new EditorLevelPropertiesPanel(onStateChanged);
    this.overlay.appendChild(this.propertiesPanel.element);

    this.colorSelector = this.initColorSelector();
    this.overlay.appendChild(this.colorSelector.element);

    this.blockerSelector = this.initBlockerSelector();
    this.overlay.appendChild(this.blockerSelector.element);

    this.utBlockerSelector = this.initUtBlockerSelector();
    this.overlay.appendChild(this.utBlockerSelector.element);

    this.blockersPanel = new EditorBlockersPanel(
      onStateChanged,
      (stackId: string | null) => getGameAreaView()?.setStackHighlight(stackId),
      () => {
        this.changeMode(headerConfig, EditMode.EditBlockerConnections);
      },
      refreshBlockerView,
    );

    this.overlay.appendChild(this.blockersPanel.element);

    this.blockerConnectionsPanel = new EditorBlockerConnectionsPanel(onStateChanged, getGameAreaView, () => {
      this.changeMode(headerConfig, EditMode.PaintBlockers);
    });
    this.overlay.appendChild(this.blockerConnectionsPanel.element);

    this.remixPanel = new EditorRemixPanel(runLevelRemix);
    this.overlay.appendChild(this.remixPanel.element);

    this.panels = [
      { panel: this.propertiesPanel, modes: [EditMode.Default] },
      { panel: this.colorSelector, modes: [EditMode.EditTiles] },
      { panel: this.blockerSelector, modes: [EditMode.PaintBlockers] },
      { panel: this.utBlockerSelector, modes: [EditMode.PaintUnderTileBlockers] },
      { panel: this.blockersPanel, modes: [EditMode.EditBlockers] },
      { panel: this.blockerConnectionsPanel, modes: [EditMode.EditBlockerConnections] },
      { panel: this.remixPanel, modes: [EditMode.Remix] },
    ];

    this.setEditorModeActive(EditMode.Default);
  }

  public destroy(): void {
    this.colorSelector.destroy();
    this.header.destroy();
    this.overlay.remove();
  }

  private initHeader(headerConfig: EditorHeaderConfig): EditorHeader {
    return new EditorHeader({
      ...headerConfig,
      onModeChange: (mode: EditMode): void => {
        this.setEditorModeActive(mode);
        headerConfig.onModeChange(mode);
      },
    });
  }

  private initColorSelector(): EditorItemSelector {
    const itemSelector = new EditorItemSelector({
      buttons: [
        { value: TileColor.None, image: './assets/images/editor/red-x.jpg', borderColor: '#ffffff' },
        { value: TileColor.Red, color: '#ff0000', borderColor: '#ffffff' },
        { value: TileColor.Green, color: '#00ff00', borderColor: '#666666' },
        { value: TileColor.Blue, color: '#4d6bff', borderColor: '#ffffff' },
        { value: TileColor.Pink, color: '#ffbddf', borderColor: '#ffffff' },
        { value: TileColor.Yellow, color: '#ffff00', borderColor: '#666666' },
        { value: TileColor.Grey, color: '#aaaaaa', borderColor: '#ffffff' },
        { value: TileColor.White, color: '#ffffff', borderColor: '#666666' },
        { value: TileColor.Navy, color: '#002fd9', borderColor: '#ffffff' },
        { value: TileColor.Purple, color: '#e017ff', borderColor: '#ffffff' },
      ],
    });
    itemSelector.setSelectionByIndex(1);
    return itemSelector;
  }

  private initBlockerSelector(): EditorItemSelector {
    const itemSelector = new EditorItemSelector({
      buttons: [
        { value: null, borderColor: '#ffffff', image: './assets/images/editor/red-x.jpg' },
        {
          value: BlockerType.Score,
          color: '#ff0000',
          borderColor: '#ffffff',
          image: './assets/images/editor/score_blocker_icon.png',
        },
        {
          value: BlockerType.Ad,
          color: '#ff0000',
          borderColor: '#ffffff',
          image: './assets/images/editor/ad_blocker_icon.png',
        },
        {
          value: BlockerType.Plank,
          color: '#ff0000',
          borderColor: '#ffffff',
          image: './assets/images/editor/plank_blocker_icon.png',
        },
        {
          value: BlockerType.Tape,
          color: '#ff0000',
          borderColor: '#ffffff',
          image: './assets/images/editor/tape_blocker_icon.png',
        },
        {
          value: BlockerType.Knot,
          color: '#ff0000',
          borderColor: '#ffffff',
          image: './assets/images/editor/knot_blocker_icon.png',
        },
        {
          value: BlockerType.BirdHouse,
          color: '#ff0000',
          borderColor: '#ffffff',
          image: './assets/images/editor/bird_blocker_icon.png',
        },
        {
          value: BlockerType.Coin,
          color: '#ff0000',
          borderColor: '#ffffff',
          image: './assets/images/editor/coin_blocker_icon.png',
        },
        {
          value: BlockerType.Rocket,
          color: '#ff0000',
          borderColor: '#ffffff',
          image: './assets/images/editor/rocket_blocker_icon.png',
        },
        {
          value: BlockerType.MeasureTape,
          color: '#ff0000',
          borderColor: '#ffffff',
          image: './assets/images/editor/measure_tape_blocker_icon.png',
        },
      ],
    });
    itemSelector.setSelectionByIndex(1);
    return itemSelector;
  }

  public initUtBlockerSelector(): EditorItemSelector {
    const itemSelector = new EditorItemSelector({
      buttons: [
        { value: null, borderColor: '#ffffff', image: './assets/images/editor/red-x.jpg' },
        {
          value: BlockerType.Star,
          color: '#ff0000',
          borderColor: '#ffffff',
          image: './assets/images/editor/star_blocker_icon.png',
        },
      ],
    });
    itemSelector.setSelectionByIndex(1);
    return itemSelector;
  }

  public setEditorModeActive(mode: EditMode): void {
    this.header.highlightEditorMode(mode);

    this.panels.forEach(({ panel, modes }) => {
      if (modes.includes(mode)) panel.focus();
      else panel.blur();
    });
  }

  public updateLevelData(levelData: LevelData): void {
    this.propertiesPanel.update(levelData);
    this.remixPanel.update(levelData);
  }

  private changeMode(headerConfig: EditorHeaderConfig, mode: EditMode): void {
    this.setEditorModeActive(mode);
    headerConfig.onModeChange(mode);
  }
}
